import { useRecoilState, useRecoilValue } from 'recoil';
import { green, purple } from '@ant-design/colors';
import { Divider, Progress, Tooltip, Typography } from 'antd';
import { css } from '@emotion/css';
import {
  annotationMetricsTallyAtom,
  timeMetricsUIAtom,
} from 'packages/lib/metrics/state';
import { FC } from 'react';
import { devModeAtom } from 'packages/at-shell/state/recoil/ui';
import { showSessionTimerState } from 'packages/at-shell/state/recoil/ui';
import { FloatingWindow } from 'packages/lib/common-ui/floating-window';

// 10-02-2026 Used Cursor (Opus 5.5) to remove inactive_time metrics events

const panelClass = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const sectionClass = css`
  display: flex;
  flex-direction: column;
`;

const strokeColor = (active: number, total: number, steps: number): string[] =>
  Array.from({ length: steps }, (_, i) => i).map((n) => {
    if (n <= steps * (active / total)) {
      return green[5] as string;
    }
    return purple[3] as string;
  });

const Prog: FC = () => {
  const { activeTime: active, elapsedClockTime: total } =
    useRecoilValue(timeMetricsUIAtom);
  if (!active || !total) return null;
  const steps = 50;
  return (
    <Progress
      steps={steps}
      strokeColor={strokeColor(
        Math.round(active / 2),
        Math.round(total / 2),
        steps
      )}
      percent={100}
      showInfo={false}
      size="small"
      style={{ margin: '0.5rem 0' }}
    />
  );
};

const roundSec = (t: number): number => Math.round(t / 1000);

const SessionTimerInternal: FC = () => {
  const dev_mode = useRecoilValue(devModeAtom);
  const tally = useRecoilValue(annotationMetricsTallyAtom);
  const { activeTime, elapsedClockTime } = useRecoilValue(timeMetricsUIAtom);
  const untrackedTime = elapsedClockTime - activeTime;
  return (
    <div className={panelClass}>
      <div className={sectionClass}>
        <Tooltip placement="right" title="Timer, for reference">
          <div>Elapsed clock time: {roundSec(elapsedClockTime)}s</div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Amount of active_time we've recorded in backend metrics API"
        >
          <div style={{ color: green[5] }}>
            Active time: {roundSec(activeTime)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Difference between elapsed clock time and recorded active time, for reference"
        >
          <div style={{ color: purple[3] }}>
            Untracked time: {untrackedTime < 0 ? 0 : roundSec(untrackedTime)}s
          </div>
        </Tooltip>
        <Tooltip
          placement="right"
          title="Visual indicator of ratio between active time and untracked time. Shows 50 steps, so 2% per step."
        >
          <div>
            <Prog />
          </div>
        </Tooltip>
      </div>
      {!dev_mode ? null : (
        <div className={sectionClass}>
          <Typography.Title
            style={{ margin: '0 0 0.25rem 0', lineHeight: '1rem' }}
            level={5}
          >
            Event Count:
          </Typography.Title>
          <Divider style={{ margin: '0.25rem 0' }} />
          {Object.entries(tally).map(([key, val]) => (
            <div key={`metrics-tally-${key}`}>
              <em>{key}:</em> <span>{val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SessionTimer: FC = () => {
  const [showSessionTimer, setShowSessionTimer] = useRecoilState(
    showSessionTimerState
  );
  return (
    <FloatingWindow
      isOpen={showSessionTimer}
      minWidth={280}
      minHeight={180}
      defaultWidth={300}
      defaultHeight={220}
      title="Session Timer"
      onClose={() => {
        setShowSessionTimer(false);
      }}
      unmountOnClose={false}
    >
      <SessionTimerInternal />
    </FloatingWindow>
  );
};
